import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import {
    SettingType,
    Widget,
    CodeBlock,
    BlockType,
    SettingOption,
    ValidationMessage,
    ValidationMessageType,
    Define,
} from '@appblocks/codegeneration/types';
import BaseWidget, { sizes } from '@appblocks/codegeneration/Widgets/BaseWidget';
import { generateFontFile } from '@appblocks/codegeneration/FontUtils';

export default class LCDTextWidget extends BaseWidget {
    constructor() {
        super();
        this.name = 'Text Widget';
        this.id = 'lcd_text_widget';
        this.minW = sizes[this.id].minW;
        this.minH = sizes[this.id].minH;

        const settings = this.getWidgetSettings(['NAME', 'COLOR', 'FONT', 'BACKGROUND_COLOR', 'FONT_SIZE', 'ALIGNMENT', 'VISIBLE', 'TEXT']);

        this.settings = this.settings.concat(settings);
    }

    getSettingOptions(settingId: string): SettingOption[] | undefined {
        switch (settingId) {
            case 'FONT':
                switch (this.project.runtime) {
                    case 'tios':
                    {
                        const fontSizes = [
                            7, 8, 9, 10, 11, 12, 14, 19,
                            18, 24, 30, 36, 48, 60, 72,
                            96, 120, 144, 160,
                        ];
                        const fonts: SettingOption[] = [];
                        for (let i = 0; i < fontSizes.length; i++) {
                            if (fontSizes[i] === 7) {
                                fonts.push({
                                    name: '7 (Small Standard Font)',
                                    value: 'LCD_SMALL_FONT',
                                });
                                continue;
                            }
                            if (fontSizes[i] === 19) {
                                fonts.push({
                                    name: '19 (Standard Font)',
                                    value: 'LCD_STD_FONT',
                                });
                                continue;
                            }
                            fonts.push({
                                name: `${fontSizes[i]}`,
                                value: `LCD_${fontSizes[i] < 19 ? 'S' : 'L'}_${fontSizes[i]}_FONT`,
                            });
                        }
                        return fonts;
                    }
                    case 'micropython':
                        return [
                            {
                                name: '8',
                                value: '8',
                            },
                        ];
                    default:

                        break;
                }
                break;
            default:
                return super.getSettingOptions(settingId);
        }
        return super.getSettingOptions(settingId);
    }

    getFiles(): Array<string> {
        if (this.project.runtime === 'simulator') {
            // already added via simulated runtime
            return [];
        }
        const files: Array<string> = [];
        const fontSize = this.getSettingValue('FONT');
        if (fontSize === 'LCD_STD_FONT' || fontSize === 'LCD_SMALL_FONT') {
            return files;
        }
        const fontFormat = fontSize.split('_')[1];
        const fontSizeValue = fontSize.split('_')[2];
        files.push(`font_${fontFormat}_${fontSizeValue}.bin`);
        return files;
    }

    validate(): Array<ValidationMessage> {
        const messages: Array<ValidationMessage> = [];
        const X = this.getSettingValue('X');
        const Y = this.getSettingValue('Y');
        const FONT = this.getSettingValue('FONT');
        const H = this.getSettingValue('H');
        const W = this.getSettingValue('W');
        const parentScreen = this.project.getParent(this);
        const displayFeature = this.project.getParent(parentScreen) || parentScreen;
        const screenWidth = displayFeature.getSettingValue('LCD_SCREEN_WIDGET_COLUMNS');
        const screenHeight = displayFeature.getSettingValue('LCD_SCREEN_WIDGET_ROWS');
        let fontSize;
        switch (FONT) {
            case 'LCD_STD_FONT':
                fontSize = 19;
                break;
            case 'LCD_SMALL_FONT':
                fontSize = 7;
                break;
            default:
                fontSize = Number(FONT.split('_')[2]);
                break;
        }
        if (Number(H) < fontSize) {
            messages.push({
                featureId: this.getID(),
                settingId: 'H',
                message: `H must be greater than or equal to ${fontSize}`,
                messageType: ValidationMessageType.UPDATE,
                data: fontSize,
            });
        }
        if (Number(X) < 0) {
            messages.push({
                featureId: this.getID(),
                settingId: 'X',
                message: 'X must be greater than 0',
                messageType: ValidationMessageType.UPDATE,
                data: 0,
            });
        }
        if (Number(Y) < 0) {
            messages.push({
                featureId: this.getID(),
                settingId: 'Y',
                message: 'Y must be greater than 0',
                messageType: ValidationMessageType.UPDATE,
                data: 0,
            });
        }
        if (Number(W) + Number(X) > screenWidth) {
            messages.push({
                featureId: this.getID(),
                settingId: 'X',
                message: `X must be less than or equal to ${screenWidth - Number(W)}`,
                messageType: ValidationMessageType.UPDATE,
                data: screenWidth - Number(W),
            });
        }
        if (Number(H) + Number(Y) > screenHeight) {
            messages.push({
                featureId: this.getID(),
                settingId: 'Y',
                message: `Y must be less than or equal to ${screenHeight - Number(H)}`,
                messageType: ValidationMessageType.UPDATE,
                data: screenHeight - Number(H),
            });
        }
        if (Number(W) > screenWidth) {
            messages.push({
                featureId: this.getID(),
                settingId: 'W',
                message: `W must be less than or equal to ${screenWidth}`,
                messageType: ValidationMessageType.UPDATE,
                data: screenWidth,
            });
        }
        if (Number(H) > screenHeight) {
            messages.push({
                featureId: this.getID(),
                settingId: 'H',
                message: `H must be less than or equal to ${screenHeight}`,
                messageType: ValidationMessageType.UPDATE,
                data: screenHeight,
            });
        }
        return messages;
    }

    getCodeBlocks(): CodeBlock[] {
        const blocks: CodeBlock[] = [];

        if (this.project.runtime === 'tios') {
            const fontSize = this.getSettingValue('FONT');
            if (fontSize === 'LCD_STD_FONT' || fontSize === 'LCD_SMALL_FONT') {
                return blocks;
            }
            const fontFormat = fontSize.split('_')[1];
            const fontSizeValue = fontSize.split('_')[2];
            const fontFileName = `font_${fontFormat}_${fontSizeValue}.bin`;
            const targetHeight = Number(fontFileName.split('_')[2].split('.')[0]);
            const baseFontFileName = targetHeight >= 19 ? '11x19(HP).bin' : 'Tibbo-5x7(V).bin';
            if (!process.env.BROWSER) {
                // eslint-disable-next-line global-require
                const fs = require('fs');
                // eslint-disable-next-line global-require
                const path = require('path');
                const baseFont = fs.readFileSync(path.join(__dirname, '..', '..', 'public', 'projectTemplates', 'tios', baseFontFileName));
                const fontFile = generateFontFile(
                    Array.from(baseFont),
                    targetHeight,
                );
                blocks.push({
                    location: fontFileName,
                    blockType: BlockType.CODE,
                    template: Buffer.from(fontFile).toString('base64'),
                });
            }
        }

        if (this.project.runtime === 'simulator') {
            let SCREEN_INDEX = 0;
            const parent = this.project.getParent(this);
            const grandParent = this.project.getParent(parent);
            const screens = grandParent.getSettingValue('SCREENS');
            for (let i = 0; i < screens.length; i++) {
                const screen = screens[i];
                const widgets = screen.getSettingValue('WIDGETS');
                for (let j = 0; j < widgets.length; j++) {
                    const widget = widgets[j];
                    if (widget.getID() === this.getID()) {
                        SCREEN_INDEX = i;
                        break;
                    }
                }
            }

            const TEXT = this.getSettingValue('TEXT');
            const COLOR = this.getSettingValue('COLOR');
            const FONT = this.getSettingValue('FONT');
            const BACKGROUND_COLOR = this.getSettingValue('BACKGROUND_COLOR');
            const FONT_SIZE = this.getSettingValue('FONT_SIZE');
            const ALIGNMENT = this.getSettingValue('ALIGNMENT');
            const VISIBLE = this.getSettingValue('VISIBLE');
            const X = this.getSettingValue('X');
            const Y = this.getSettingValue('Y');
            const W = this.getSettingValue('W');
            const H = this.getSettingValue('H');

            blocks.push({
                location: 'display_init_widgets',
                blockType: BlockType.CODE,
                template: `
simulator.updateContext("${this.getID()}", {
    TEXT: "${TEXT}",
    COLOR: "${COLOR}",
    FONT: "${FONT}",
    BACKGROUND_COLOR: "${BACKGROUND_COLOR}",
    ALIGNMENT: "${ALIGNMENT}",
    VISIBLE: ${VISIBLE},
    X: ${X},
    Y: ${Y},
    W: ${W},
    H: ${H},
    SCREEN_INDEX: ${SCREEN_INDEX},
})
`,
            });
        }

        return blocks;
    }
}
